import { getNlsString } from '../../nls/nlsRuntime'
import type { NlsInterpolationValues } from '../../nls/nlsTypes'
import type { NlsKey } from '../../nls/strings_nls'
import type { QuotaTargetType } from '../../types/quota'
import type { ResourceSignalKind } from '../../types/resource'
import type { StockStatus } from '../../types/stock'

export type ResourcesNlsT = (key: NlsKey, values?: NlsInterpolationValues) => string

const defaultT: ResourcesNlsT = (key, values) => getNlsString(key, values)

export type BudgetHubTabNls = 'DASHBOARD' | 'LEDGER' | 'INTAKE'
export type QuotaHubTabNls = 'LEADERBOARD' | 'MANAGEMENT' | 'HISTORY'
export type BudgetEntityTypeNls = 'ALL' | 'GOAL' | 'TASK' | 'CHECKLIST'

const BUDGET_HUB_TAB_KEYS: Record<BudgetHubTabNls, NlsKey> = {
  DASHBOARD: 'NLSResourcesBudgetTabDashboard',
  LEDGER: 'NLSResourcesBudgetTabLedger',
  INTAKE: 'NLSResourcesBudgetTabIntake',
}

const QUOTA_HUB_TAB_KEYS: Record<QuotaHubTabNls, NlsKey> = {
  LEADERBOARD: 'NLSResourcesQuotaTabLeaderboard',
  MANAGEMENT: 'NLSResourcesQuotaTabManagement',
  HISTORY: 'NLSResourcesQuotaTabHistory',
}

const STOCK_STATUS_KEYS: Record<'ALL' | StockStatus, NlsKey> = {
  ALL: 'NLSResourcesStockStatusAll',
  Available: 'NLSResourcesStockStatusAvailable',
  'In-Use': 'NLSResourcesStockStatusInUse',
  Maintenance: 'NLSResourcesStockStatusMaintenance',
}

const QUOTA_TARGET_LABEL_KEYS: Record<QuotaTargetType, { full: NlsKey; short: NlsKey }> = {
  tasks_completed: { full: 'NLSResourcesQuotaTargetTasksCompleted', short: 'NLSResourcesQuotaTargetTasks' },
  sales_units: { full: 'NLSResourcesQuotaTargetUnitsSold', short: 'NLSResourcesQuotaTargetUnitsSold' },
  checklists_done: { full: 'NLSResourcesQuotaTargetChecklistsDone', short: 'NLSResourcesQuotaTargetChecklists' },
  weighted_progress: { full: 'NLSResourcesQuotaTargetWeightedProgress', short: 'NLSResourcesQuotaTargetWeighted' },
  hours_logged: { full: 'NLSResourcesQuotaTargetHoursLogged', short: 'NLSResourcesQuotaTargetHours' },
}

const BUDGET_ENTITY_TYPE_KEYS: Record<BudgetEntityTypeNls, NlsKey> = {
  ALL: 'NLSResourcesEntityAll',
  GOAL: 'NLSResourcesEntityGoal',
  TASK: 'NLSResourcesEntityTask',
  CHECKLIST: 'NLSResourcesEntityChecklist',
}

const RESOURCE_SIGNAL_LABEL_KEYS: Record<ResourceSignalKind, NlsKey> = {
  budget: 'NLSResourcesSignalBudgetRisk',
  stock: 'NLSResourcesSignalStockShort',
  quota: 'NLSResourcesSignalQuotaImpact',
}

export const getBudgetHubTabLabel = (tab: BudgetHubTabNls, t: ResourcesNlsT = defaultT) =>
  t(BUDGET_HUB_TAB_KEYS[tab])

export const getQuotaHubTabLabel = (tab: QuotaHubTabNls, t: ResourcesNlsT = defaultT) =>
  t(QUOTA_HUB_TAB_KEYS[tab])

export const getStockStatusLabel = (status: 'ALL' | StockStatus, t: ResourcesNlsT = defaultT) =>
  t(STOCK_STATUS_KEYS[status])

export const getQuotaTargetLabel = (
  type: QuotaTargetType,
  variant: 'full' | 'short' = 'full',
  t: ResourcesNlsT = defaultT,
) => t(QUOTA_TARGET_LABEL_KEYS[type][variant])

export const getBudgetEntityTypeLabel = (type: BudgetEntityTypeNls | string, t: ResourcesNlsT = defaultT) =>
  Object.prototype.hasOwnProperty.call(BUDGET_ENTITY_TYPE_KEYS, type)
    ? t(BUDGET_ENTITY_TYPE_KEYS[type as BudgetEntityTypeNls])
    : type

export const getResourceSignalLabel = (kind: ResourceSignalKind, t: ResourcesNlsT = defaultT) =>
  t(RESOURCE_SIGNAL_LABEL_KEYS[kind])
